import { uniq } from 'lodash';

import { buildScopeRows } from '~/components/SystemConfiguration/CliveConfiguration/scope';
import type { OrganizationModel } from '~/components/types/organization-types';
import type { ClaimFieldsFormsResponseClaimFieldsFormResponse } from '~/server_shared/openapi/data-contracts';

export type ClaimBarForm = ClaimFieldsFormsResponseClaimFieldsFormResponse;

export const DEFAULT_SCOPE_KEY = 'default';

const scopeKey = (lob: string, subOrganizationId: number | null) => `${lob}:${subOrganizationId ?? ''}`;

/** A scope a claim-bar configuration can exist for, with that configuration if it has one. */
export interface ConfigurationScope {
  key: string;
  /** Null only for the Default. */
  lob: string | null;
  subOrganizationId: number | null;
  label: string;
  form: ClaimBarForm | undefined;
  /** Whether the organization still supports this scope's LOB there, which the server's create requires. */
  isOffered: boolean;
}

export interface ConfigurationScopeGroup {
  lob: string;
  label: string;
  /**
   * The LOB's own scope, offered only to an organization without sub-organizations. With sub-organizations a
   * configuration is a sub-organization and a LOB; a LOB-wide level above them would be an override admins
   * do not expect.
   */
  lobOnly?: ConfigurationScope;
  subOrganizations: ConfigurationScope[];
}

/**
 * The Default, then one group per supported LOB: the LOB's own scope for an organization without
 * sub-organizations, and one scope per sub-organization supporting it. A sub-organization configuration whose scope
 * the organization no longer offers is still listed under its LOB. A LOB-wide configuration of an organization with
 * sub-organizations has no row, and a LOB left with no row has no group.
 */
export const buildConfigurationScopes = (
  organization: OrganizationModel,
  forms: ClaimBarForm[],
  getLobLabel: (lob: string) => string
): { defaultScope: ConfigurationScope; groups: ConfigurationScopeGroup[] } => {
  const formsByKey = new Map(
    forms.flatMap((form) => (form.lob === null ? [] : [[scopeKey(form.lob, form.sub_organization_id), form] as const]))
  );
  const subOrganizationName = (id: number) =>
    organization.sub_organizations?.find((subOrganization) => subOrganization.id === id)?.name ?? String(id);
  // buildScopeRows writes '' for a supported-LOB row that names no LOB; there is no scope to offer for it.
  const scopeRows = buildScopeRows(organization).filter((row) => row.lob !== '');
  const offeredKeys = new Set(scopeRows.map((row) => scopeKey(row.lob, row.sub_organization_id)));
  const toScope = (lob: string, subOrganizationId: number | null): ConfigurationScope => ({
    key: scopeKey(lob, subOrganizationId),
    lob,
    subOrganizationId,
    label: subOrganizationId === null ? getLobLabel(lob) : subOrganizationName(subOrganizationId),
    form: formsByKey.get(scopeKey(lob, subOrganizationId)),
    isOffered: offeredKeys.has(scopeKey(lob, subOrganizationId)),
  });

  const offeredPairs = scopeRows.filter((row) => row.sub_organization_id !== null);
  const configuredPairs = forms.filter((form) => form.lob !== null && form.sub_organization_id !== null);
  // A flat organization's rows carry no sub-organization, and their LOBs are its own; otherwise the
  // LOBs are those its sub-organizations support, as the server's own create check reads them.
  const lobs = uniq([
    ...scopeRows.map((row) => row.lob),
    ...forms.flatMap((form) => (form.lob === null ? [] : [form.lob])),
  ]);

  const groups = lobs
    .map((lob) => ({
      lob,
      label: getLobLabel(lob),
      lobOnly: organization.sub_organizations_enabled ? undefined : toScope(lob, null),
      subOrganizations: uniq([
        ...offeredPairs.filter((row) => row.lob === lob).map((row) => row.sub_organization_id as number),
        ...configuredPairs.filter((form) => form.lob === lob).map((form) => form.sub_organization_id as number),
      ]).map((subOrganizationId) => toScope(lob, subOrganizationId)),
    }))
    // A LOB-wide configuration of an organization with sub-organizations has no row, so it leaves no heading either.
    .filter((group) => group.lobOnly !== undefined || group.subOrganizations.length > 0);

  return {
    defaultScope: {
      key: DEFAULT_SCOPE_KEY,
      lob: null,
      subOrganizationId: null,
      label: '',
      form: forms.find((form) => form.is_default),
      isOffered: true,
    },
    groups,
  };
};

/** Sub-organization scopes whose name contains `query`; a group with none left is dropped. */
export const filterScopeGroups = (groups: ConfigurationScopeGroup[], query: string): ConfigurationScopeGroup[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return groups;
  }
  return groups
    .map((group) => ({
      ...group,
      subOrganizations: group.subOrganizations.filter((scope) => scope.label.toLowerCase().includes(needle)),
    }))
    .filter((group) => group.subOrganizations.length > 0);
};

/**
 * "X of Y customized": every scope the user may open except the Default, and of those the ones with a configuration
 * of their own. A scope the user may not open is not counted at all, since whether it is customized is its data.
 */
export const countCustomizedScopes = (
  groups: ConfigurationScopeGroup[],
  isOpenable: (scope: ConfigurationScope) => boolean
): { customized: number; total: number } => {
  const scopes = groups
    .flatMap((group) => [...(group.lobOnly ? [group.lobOnly] : []), ...group.subOrganizations])
    .filter(isOpenable);
  return { customized: scopes.filter((scope) => scope.form !== undefined).length, total: scopes.length };
};

/**
 * Whether the user's own restriction lets them open this scope, read as the server's own scope check reads it: an
 * empty restriction is none, and a LOB's own scope reaches sub-organizations outside a sub-organization
 * restriction, so that restriction excludes it. Every administrator opens the Default.
 */
const isWithinUserRestriction = (
  scope: ConfigurationScope,
  user: { sub_organization_ids?: number[] | null; lobs?: string[] | null }
): boolean => {
  if (scope.lob === null) {
    return true;
  }
  const subOrganizationIds = user.sub_organization_ids ?? [];
  if (
    subOrganizationIds.length > 0 &&
    (scope.subOrganizationId === null || !subOrganizationIds.includes(scope.subOrganizationId))
  ) {
    return false;
  }
  const lobs = user.lobs ?? [];
  return lobs.length === 0 || lobs.includes(scope.lob);
};

/**
 * Whether the user may open this scope. The server checks a super-organization caller as the target organization's
 * system user, whose restriction the client cannot see, so only a same-organization user's own restriction applies.
 */
export const isInCallerScope = (
  scope: ConfigurationScope,
  user: { sub_organization_ids?: number[] | null; lobs?: string[] | null },
  userOrganization: { is_super_organization?: boolean } | null | undefined
): boolean => userOrganization?.is_super_organization === true || isWithinUserRestriction(scope, user);
